import {submitSheetOrder} from './wearse-sheet-submit.js';

const pendingKey = 'wearse-pantalon-pending';
const receiptKey = 'wearse-pantalon-sheet-receipt';

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (/^0[5-7]\d{8}$/.test(digits)) return '+212' + digits.slice(1);
  if (/^[5-7]\d{8}$/.test(digits)) return '+212' + digits;
  if (/^212[5-7]\d{8}$/.test(digits)) return '+' + digits;
  return '';
}

class WearseLanding extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.config = JSON.parse(this.querySelector('[data-wearse-config]').textContent);
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.error = this.querySelector('.wr-error');
    this.success = this.querySelector('.wr-success');
    this.submitButton = this.querySelector('.wr-submit');
    this.originalButtonText = this.submitButton.textContent;
    this.quantity = Number(this.form.elements.offer?.value) || 1;
    this.colors = this.config.colors || ['Noir', 'Bleu marine', 'Marron', 'Beige clair'];
    this.sizes = this.config.sizes || ['S', 'M', 'L', 'XL', 'XXL'];
    this.selections = [];
    this.controller = new AbortController();
    const signal = this.controller.signal;

    this.setupVideos(signal);
    this.form.addEventListener('submit', (event) => this.submit(event), {signal});
    this.form.addEventListener('input', (event) => {
      if (event.target.matches('input:not([type=radio])')) {
        event.target.removeAttribute('aria-invalid');
        event.target.setCustomValidity('');
      }
    }, {signal});
    this.form.addEventListener('change', (event) => {
      if (event.target.name !== 'offer') return;
      this.quantity = Number(event.target.value) || 1;
      this.renderVariants();
      this.updateTotal();
      this.error.hidden = true;
      this.success.hidden = true;
    }, {signal});

    this.querySelectorAll('[data-color]').forEach((button) => button.addEventListener('click', () => {
      if (this.busy || this.pendingPayload || this.completed) return;
      const desired = button.dataset.color === 'Beige' ? 'Beige clair' : button.dataset.color;
      if (this.colors.includes(desired)) {
        this.selections[0] = {
          color: desired,
          size: this.selections[0]?.size || this.config.defaultSize || 'M',
        };
        this.renderVariants();
      }
      this.form.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      this.form.querySelector('input')?.focus({preventScroll: true});
    }, {signal}));

    this.renderVariants();
    this.updateTotal();
    this.restorePending();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.initialized = false;
  }

  setupVideos(signal) {
    this.querySelectorAll('.wr-video-media').forEach((media) => {
      const video = media.querySelector('video');
      const play = media.querySelector('.wr-reel-play');
      const error = media.querySelector('.wr-reel-error');
      if (!video || !play) return;
      video.tabIndex = 0;
      play.hidden = !video.paused;
      const sync = () => {
        play.hidden = !video.paused;
        if (!video.paused) video.dataset.started = 'true';
      };
      video.addEventListener('play', sync, {signal});
      video.addEventListener('pause', sync, {signal});
      video.addEventListener('ended', sync, {signal});
      play.addEventListener('click', async () => {
        play.disabled = true;
        error.hidden = true;
        try {
          await video.play();
          video.focus({preventScroll: true});
        } catch {
          error.textContent = 'La vidéo ne peut pas être chargée. Réessayez dans un instant.';
          error.hidden = false;
          play.hidden = false;
        } finally {
          play.disabled = false;
        }
      }, {signal});
    });
  }

  money(cents) {
    return new Intl.NumberFormat('fr-MA', {maximumFractionDigits: 2}).format(cents / 100) + ' DH';
  }

  showError(message) {
    this.success.hidden = true;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  renderVariants() {
    const container = this.querySelector('[data-variant-rows]');
    container.replaceChildren();

    for (let index = 0; index < this.quantity; index += 1) {
      this.selections[index] ||= {
        color: this.config.defaultColor || 'Marron',
        size: this.config.defaultSize || 'M',
      };
      const selection = this.selections[index];
      if (!this.colors.includes(selection.color)) selection.color = this.colors[0];
      if (!this.sizes.includes(selection.size)) selection.size = this.sizes[0];

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'wr-variant-row';
      const legend = document.createElement('legend');
      legend.textContent = 'Pantalon ' + (index + 1);
      fieldset.append(legend);

      const inner = document.createElement('div');
      inner.className = 'wr-variant-inner';
      const image = document.createElement('img');
      image.src = this.config.images[selection.color] || '';
      image.alt = 'Pantalon ' + selection.color;
      image.width = 90;
      image.height = 130;
      inner.append(image);

      const controls = document.createElement('div');
      controls.className = 'wr-variant-options';
      for (const [kind, label, values] of [['color', 'Couleur', this.colors], ['size', 'Taille', this.sizes]]) {
        const id = this.form.id + '-' + kind + '-' + index;
        const labelElement = document.createElement('label');
        labelElement.htmlFor = id;
        labelElement.textContent = label;
        const select = document.createElement('select');
        select.id = id;
        select.name = kind + '_' + (index + 1);
        select.required = true;

        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          const sizeLabels = {S: '30', M: '32', L: '34', XL: '36', XXL: '38'};
          option.textContent = kind === 'size' && sizeLabels[value] ? sizeLabels[value] + ' / ' + value : value;
          option.selected = value === selection[kind];
          select.append(option);
        });

        select.addEventListener('change', () => {
          selection[kind] = select.value;
          this.renderVariants();
          this.updateTotal();
          this.querySelector('#' + CSS.escape(id))?.focus();
        });
        controls.append(labelElement, select);
      }

      inner.append(controls);
      fieldset.append(inner);
      container.append(fieldset);
    }
  }

  updateTotal() {
    const amount = Number(this.config.prices[this.quantity - 1]) || 0;
    this.querySelector('[data-total]').textContent = this.money(amount);
    this.querySelector('[data-total-label]').textContent = 'Total à la livraison :';
    this.querySelector('[data-summary-note]').textContent = '';
  }

  validate() {
    let valid = true;
    for (const name of ['fullName', 'phone', 'city', 'address']) {
      const input = this.form.elements[name];
      const ok = name === 'phone' ? Boolean(normalizePhone(input.value)) : input.value.trim().length >= 2;
      input.setAttribute('aria-invalid', String(!ok));
      if (!ok) {
        if (valid) input.focus();
        valid = false;
      }
    }
    if (!valid) this.showError('Renseignez votre nom, votre ville, votre adresse et un numéro marocain valide (exemple : 0612345678).');
    return valid;
  }

  buildPayload() {
    const submittedAt = new Date().toISOString();
    const amountCents = Number(this.config.prices[this.quantity - 1]) || 0;
    const pants = this.selections.slice(0, this.quantity).map((selection, index) => ({
      number: index + 1,
      product: 'Pantalon WEARSE',
      color: selection.color,
      size: selection.size,
    }));
    const params = new URLSearchParams(window.location.search);
    const payload = {
      landing: 'offre-pantalon',
      landing_page: 'offre-pantalon',
      sheet_name: 'offre-pantalon',
      sheet: 'offre-pantalon',
      worksheet: 'offre-pantalon',
      orderNumber: 'WEARSE-PANTALON-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8).toUpperCase(),
      submitted_at: submittedAt,
      dateLabel: new Intl.DateTimeFormat('fr-MA', {dateStyle: 'short', timeStyle: 'short'}).format(new Date()),
      fullName: this.form.elements.fullName.value.trim(),
      phone: normalizePhone(this.form.elements.phone.value),
      city: this.form.elements.city.value.trim(),
      address: this.form.elements.address.value.trim(),
      offer: this.quantity + ' pantalon' + (this.quantity > 1 ? 's' : ''),
      offer_quantity: this.quantity,
      item_count: this.quantity,
      price: this.money(amountCents),
      total_price: amountCents,
      currency: this.config.currency || 'MAD',
      payment_method: 'cash_on_delivery',
      pants,
      pants_json: JSON.stringify(pants),
      source: window.location.href,
      userAgent: navigator.userAgent,
      status: 'Nouveau',
      utm_source: params.get('utm_source') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_medium: params.get('utm_medium') || '',
      fbclid: params.get('fbclid') || '',
    };

    pants.forEach((item) => {
      payload['color_' + item.number] = item.color;
      payload['size_' + item.number] = item.size;
    });
    return payload;
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || this.completed || (!this.pendingPayload && !this.validate())) return;
    this.error.hidden = true;
    this.success.hidden = true;

    if (this.config.preview || !this.config.enabled) {
      this.showError('La commande en ligne sera disponible prochainement. Veuillez réessayer ultérieurement.');
      return;
    }
    if (!this.config.endpoint) {
      this.showError('Le service de commande est indisponible. Veuillez réessayer dans quelques instants.');
      return;
    }

    this.busy = true;
    this.submitButton.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.submitButton.textContent = 'Envoi en cours…';
    this.pendingPayload ||= this.buildPayload();
    this.pendingPayload.request_id = this.pendingPayload.orderNumber;
    try { sessionStorage.setItem(pendingKey, JSON.stringify(this.pendingPayload)); } catch { /* In-memory retries remain available. */ }
    const lockedControls = [...this.form.querySelectorAll('input, select')];
    lockedControls.forEach((element) => { element.disabled = true; });

    try {
      const payload = this.pendingPayload;
      await submitSheetOrder(this.config.endpoint, payload);
      try {
        sessionStorage.setItem(receiptKey, JSON.stringify({
          kind: 'sheet', reference: payload.orderNumber, total: payload.total_price / 100,
          currency: payload.currency, createdAt: Date.now(),
        }));
        sessionStorage.removeItem(pendingKey);
      } catch { /* Confirmation still succeeds when browser storage is unavailable. */ }
      this.completed = true;
      this.submitButton.textContent = 'Commande enregistrée';
      this.success.textContent = 'Merci, votre commande a bien été enregistrée. L’équipe WEARSE vous contactera pour confirmer la livraison.';
      this.success.hidden = false;
      this.success.focus();
      const destination = new URL(this.config.thankYouUrl || '/pages/merci-commande-pantalon-fr', window.location.origin);
      if (destination.origin !== window.location.origin) throw new Error('Invalid confirmation URL');
      const link = document.createElement('a');
      link.href = destination.href;
      link.textContent = ' Voir la confirmation →';
      this.success.append(link);
      window.location.assign(destination.href);
    } catch {
      if (!this.completed) this.showError('La confirmation n’a pas encore été reçue. Réessayez avec le bouton ci-dessous : les mêmes informations et le même numéro de demande seront renvoyés.');
    } finally {
      this.busy = false;
      this.submitButton.disabled = Boolean(this.completed);
      lockedControls.forEach((element) => { element.disabled = Boolean(this.completed || this.pendingPayload); });
      this.form.removeAttribute('aria-busy');
      if (!this.completed) this.submitButton.textContent = 'Réessayer la confirmation';
    }
  }

  restorePending() {
    try {
      const payload = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
      if (!payload) return;
      const age = Date.now() - Date.parse(payload.submitted_at);
      if (!(age >= 0 && age < 86400000) || !payload.orderNumber || !Array.isArray(payload.pants)) {
        sessionStorage.removeItem(pendingKey);
        return;
      }
      this.pendingPayload = payload;
      this.quantity = payload.offer_quantity;
      this.selections = payload.pants.map(({color, size}) => ({color, size}));
      for (const name of ['fullName', 'phone', 'city', 'address']) this.form.elements[name].value = payload[name];
      this.form.querySelectorAll('[name="offer"]').forEach(input => { input.checked = Number(input.value) === this.quantity; });
      this.renderVariants();
      this.updateTotal();
      this.form.querySelectorAll('input, select').forEach(input => { input.disabled = true; });
      this.submitButton.textContent = 'Réessayer la confirmation';
      this.showError('Une demande attend sa confirmation. Cliquez ci-dessous pour réessayer avec le même numéro de demande.');
    } catch { /* Storage is optional. */ }
  }
}

if (!customElements.get('wearse-landing')) customElements.define('wearse-landing', WearseLanding);
